import React, { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { trpc } from '@client-trpc';
import {
  AppGrid,
  AppGridColumn
} from '@client/foundation/shared/components/app-grid';
import type { TemplateType } from '@server/trading-templates/schemas/input.schema';
import type {
  SharedResourceSummary,
  SharedResourceType
} from '@server/shared-resources/schemas';

type SharedResourcesTableProps<Row extends SharedResourceSummary> = {
  rows: Row[];
  columns: AppGridColumn<Row>[];
  onImport: (row: Row) => void;
  onModify?: (row: Row) => void;
  onRevoke?: (row: Row) => void;
  paperSx?: SxProps<Theme>;
};

type SharedResourcesGridProps = {
  resourceType: SharedResourceType;
  templateType?: TemplateType;
  onImport: (sharedResourceId: string, sharedResourceName: string) => void;
  onModify?: (
    sharedResourceId: string,
    sharedResourceName: string,
    sharedResourceNotes?: string
  ) => void;
  onRevoke?: (sharedResourceId: string) => void;
};

const formatDateTime = (value: string) => new Date(value).toLocaleString();

const baseColumns: AppGridColumn<SharedResourceSummary>[] = [
  {
    key: 'name',
    header: 'Name',
    renderCell: (row) => row.rootName
  },
  {
    key: 'owner',
    header: 'Owner',
    renderCell: (row) => row.ownerEmail
  },
  {
    key: 'updatedAt',
    header: 'Updated',
    renderCell: (row) => formatDateTime(row.updatedAt)
  }
];

const SharedResourcesTable = <Row extends SharedResourceSummary>({
  rows,
  columns,
  onImport,
  onModify,
  onRevoke,
  paperSx
}: SharedResourcesTableProps<Row>) => {
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  const selectedRow = useMemo(
    () => rows.find((row) => row.id === selectedRowIds[0]),
    [rows, selectedRowIds]
  );

  const canModify = Boolean(selectedRow && selectedRow.isOwner && onModify);
  const canRevoke = Boolean(selectedRow && selectedRow.isOwner && onRevoke);

  const handleClear = () => {
    setSelectedRowIds([]);
  };

  const actions = useMemo(
    () => [
      {
        key: 'clear',
        label: 'Clear',
        tone: 'secondary' as const,
        disabled: selectedRowIds.length === 0,
        onClick: handleClear
      },
      {
        key: 'revoke',
        label: 'Revoke',
        tone: 'danger' as const,
        disabled: !canRevoke,
        onClick: () => {
          if (selectedRow && onRevoke) {
            onRevoke(selectedRow);
          }
        }
      },
      {
        key: 'modify',
        label: 'Modify',
        disabled: !canModify,
        onClick: () => {
          if (selectedRow && onModify) {
            onModify(selectedRow);
          }
        }
      },
      {
        key: 'import',
        label: 'Import',
        disabled: selectedRowIds.length !== 1,
        onClick: () => {
          if (selectedRow) {
            onImport(selectedRow);
          }
        }
      }
    ],
    [
      canModify,
      canRevoke,
      onImport,
      onModify,
      onRevoke,
      selectedRow,
      selectedRowIds.length
    ]
  );

  return (
    <AppGrid
      rows={rows}
      columns={columns}
      getRowId={(row) => row.id}
      selectedRowIds={selectedRowIds}
      onSelectedRowIdsChange={setSelectedRowIds}
      multiSelect={false}
      actions={actions}
      paperSx={paperSx}
    />
  );
};

export const SharedResourcesGrid: React.FC<SharedResourcesGridProps> = ({
  resourceType,
  templateType,
  onImport,
  onModify,
  onRevoke
}) => {
  const columns = useMemo<AppGridColumn<SharedResourceSummary>[]>(() => {
    if (resourceType === 'template') {
      return baseColumns;
    }

    return [
      ...baseColumns,
      {
        key: 'notes',
        header: 'Notes',
        renderCell: (row) =>
          row.notes ? (
            <Box
              sx={{
                maxWidth: 360,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere'
              }}
            >
              {row.notes}
            </Box>
          ) : null
      }
    ];
  }, [resourceType]);

  const { data: rows = [] } = trpc.sharedResources.listSharedResources.useQuery(
    {
      resourceType,
      templateType
    }
  );

  return (
    <SharedResourcesTable
      rows={rows}
      columns={columns}
      onImport={(row) => onImport(row.id, row.rootName)}
      onModify={
        onModify
          ? (row) => onModify(row.id, row.rootName, row.notes)
          : undefined
      }
      onRevoke={onRevoke ? (row) => onRevoke(row.id) : undefined}
    />
  );
};
